import { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Col, Form, Row } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

function authConfig(token, json = false) {
  const headers = { Authorization: `Bearer ${token}` };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function clockLabel(value) {
  if (!value) {
    return "";
  }
  const [hourText, minute = "00"] = value.split(":");
  let hour = Number(hourText);
  if (!Number.isFinite(hour)) {
    return "";
  }
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
}

function lunchHours(lunch) {
  const start = clockLabel(lunch.start_time);
  const end = clockLabel(lunch.end_time);
  if (start && end) {
    return `${start} – ${end}`;
  }
  if (start) {
    return `Starts ${start}`;
  }
  if (end) {
    return `Ends ${end}`;
  }
  return "";
}

function inputTime(value) {
  return (value || "").slice(0, 5);
}

function TicketRow({ ticket, token, onChanged }) {
  const [name, setName] = useState(ticket.name);
  const [price, setPrice] = useState(ticket.price);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setName(ticket.name);
    setPrice(ticket.price);
  }, [ticket]);

  const save = async () => {
    setError("");
    try {
      await axios.put(
        `/api/admin/vip-ticket-types/${ticket.id}/`,
        { name, price },
        authConfig(token, true)
      );
      await onChanged("Ticket saved.");
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    }
  };

  return (
    <article className="admin-record">
      <div className="admin-record-copy">
        <strong>{ticket.name}</strong>
        <span>${Number(ticket.price).toFixed(2)}</span>
      </div>
      <RecordActions
        name={ticket.name}
        onEdit={() => {
          setName(ticket.name);
          setPrice(ticket.price);
          setError("");
          setEditing(true);
        }}
        onRemove={() => onChanged(null, ticket.id, "ticket type", ticket.name)}
      />
      <AdminFormModal show={editing} title={`Edit ${ticket.name}`} onHide={() => setEditing(false)}>
        <Form
          onSubmit={async (event) => {
            event.preventDefault();
            if (await save()) {
              setEditing(false);
            }
          }}
        >
          <Form.Group className="mb-3" controlId={`ticket-edit-${ticket.id}`}>
            <Form.Label>Name</Form.Label>
            <Form.Control value={name} onChange={(e) => setName(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3" controlId={`ticket-price-edit-${ticket.id}`}>
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </Form.Group>
          {error ? <Message variant="danger">{error}</Message> : null}
          <AdminFormActions saveLabel="Save ticket" onCancel={() => setEditing(false)} />
        </Form>
      </AdminFormModal>
    </article>
  );
}

function LunchEditor({ lunch, token, onChanged, startOpen }) {
  const [title, setTitle] = useState(lunch.title);
  const [hosted, setHosted] = useState(lunch.hosted || "");
  const [startTime, setStartTime] = useState(inputTime(lunch.start_time));
  const [endTime, setEndTime] = useState(inputTime(lunch.end_time));
  const [menu, setMenu] = useState(null);
  const [logo, setLogo] = useState(null);
  const [ticketName, setTicketName] = useState("");
  const [ticketPrice, setTicketPrice] = useState("");
  const [error, setError] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const [editing, setEditing] = useState(Boolean(startOpen));
  const [addingTicket, setAddingTicket] = useState(false);

  useEffect(() => {
    setTitle(lunch.title);
    setHosted(lunch.hosted || "");
    setStartTime(inputTime(lunch.start_time));
    setEndTime(inputTime(lunch.end_time));
  }, [lunch]);

  const saveLunch = async (event) => {
    event.preventDefault();
    setError("");
    const formData = new FormData();
    formData.append("title", title);
    formData.append("hosted", hosted);
    formData.append("start_time", startTime);
    formData.append("end_time", endTime);
    if (menu) {
      formData.append("menu", menu);
    }
    if (logo) {
      formData.append("logo", logo);
    }
    try {
      await axios.put(`/api/admin/vip-lunches/${lunch.id}/`, formData, authConfig(token));
      setMenu(null);
      setLogo(null);
      setFileKey((current) => current + 1);
      setEditing(false);
      await onChanged("Luncheon saved.");
    } catch (err) {
      setError(errorText(err));
    }
  };

  const addTicket = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await axios.post(
        "/api/admin/vip-ticket-types/",
        { vip_lunch: lunch.id, name: ticketName, price: ticketPrice },
        authConfig(token, true)
      );
      setTicketName("");
      setTicketPrice("");
      setAddingTicket(false);
      await onChanged("Ticket type saved.");
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <section className="admin-group">
      <article className="lunch-card">
        <div className="lunch-card-head">
          <div className="admin-record-copy">
            <strong>{lunch.title}</strong>
            <span>{lunch.event_title || "Not attached to an event"}</span>
            {lunch.hosted ? <span>Hosted by {lunch.hosted}</span> : null}
            {lunchHours(lunch) ? <span>{lunchHours(lunch)}</span> : null}
          </div>
          <RecordActions
            name={lunch.title}
            onEdit={() => {
              setTitle(lunch.title);
              setHosted(lunch.hosted || "");
              setStartTime(inputTime(lunch.start_time));
              setEndTime(inputTime(lunch.end_time));
              setEditing(true);
            }}
            onRemove={() => onChanged(null, lunch.id, "luncheon", lunch.title)}
          />
        </div>
        <div className="lunch-tickets">
          <div className="lunch-tickets-head">
            <h3>Ticket types</h3>
            <AdminAddButton onClick={() => setAddingTicket(true)}>Add a ticket type</AdminAddButton>
          </div>
          {lunch.ticket_types.length === 0 ? (
            <p>No ticket types yet.</p>
          ) : (
            lunch.ticket_types.map((ticket) => (
              <TicketRow key={ticket.id} ticket={ticket} token={token} onChanged={onChanged} />
            ))
          )}
        </div>
      </article>
      <AdminFormModal show={editing} size="lg" title={`Edit ${lunch.title}`} onHide={() => setEditing(false)}>
        {error && <Message variant="danger">{error}</Message>}
        <Form onSubmit={saveLunch}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-title-${lunch.id}`}>
                <Form.Label>Title</Form.Label>
                <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-host-${lunch.id}`}>
                <Form.Label>Host</Form.Label>
                <Form.Control value={hosted} onChange={(e) => setHosted(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-start-${lunch.id}`}>
                <Form.Label>Starts</Form.Label>
                <Form.Control type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-end-${lunch.id}`}>
                <Form.Label>Ends</Form.Label>
                <Form.Control type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-menu-${lunch.id}`}>
                <Form.Label>Menu</Form.Label>
                {lunch.menu_url ? (
                  <div className="mb-2">
                    <a href={lunch.menu_url} target="_blank" rel="noreferrer">
                      Current menu
                    </a>
                  </div>
                ) : null}
                <Form.Control
                  key={`menu-${lunch.id}-${fileKey}`}
                  type="file"
                  onChange={(e) => setMenu(e.target.files?.[0] || null)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`lunch-logo-${lunch.id}`}>
                <Form.Label>Logo</Form.Label>
                {lunch.logo_url ? (
                  <img className="event-photo d-block mb-2" src={lunch.logo_url} alt="" />
                ) : null}
                <Form.Control
                  key={`logo-${lunch.id}-${fileKey}`}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogo(e.target.files?.[0] || null)}
                />
                <p className="field-note">If you upload a new image, it replaces the current one.</p>
              </Form.Group>
            </Col>
          </Row>
          <AdminFormActions saveLabel="Save luncheon" onCancel={() => setEditing(false)} />
        </Form>
      </AdminFormModal>
      <AdminFormModal show={addingTicket} title="Add a ticket type" onHide={() => setAddingTicket(false)}>
        <Form onSubmit={addTicket}>
          <Form.Group className="mb-3" controlId={`ticket-name-${lunch.id}`}>
            <Form.Label>Name</Form.Label>
            <Form.Control value={ticketName} onChange={(e) => setTicketName(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3" controlId={`ticket-price-${lunch.id}`}>
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={ticketPrice}
              onChange={(e) => setTicketPrice(e.target.value)}
              required
            />
          </Form.Group>
          {error ? <Message variant="danger">{error}</Message> : null}
          <AdminFormActions saveLabel="Add ticket type" onCancel={() => setAddingTicket(false)} />
        </Form>
      </AdminFormModal>
    </section>
  );
}

function AdminVip({ token }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [lunches, setLunches] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ event: "", title: "", hosted: "", start_time: "", end_time: "" });
  const [menu, setMenu] = useState(null);
  const [logo, setLogo] = useState(null);
  const [ask, confirmDialog] = useConfirm();
  const [pendingEdit, setPendingEdit] = useState("");

  const load = async () => {
    const config = authConfig(token);
    const [eventData, lunchData] = await Promise.all([
      axios.get("/api/admin/events/", config),
      axios.get("/api/admin/vip-lunches/", config),
    ]);
    setEvents(eventData.data);
    setLunches(lunchData.data);
  };

  useEffect(() => {
    const config = authConfig(token);
    Promise.all([
      axios.get("/api/admin/events/", config),
      axios.get("/api/admin/vip-lunches/", config),
    ])
      .then(([eventData, lunchData]) => {
        setEvents(eventData.data);
        setLunches(lunchData.data);
      })
      .catch((err) => setError(errorText(err)));
  }, [token]);

  useEffect(() => {
    const add = searchParams.get("add") === "1";
    const edit = searchParams.get("edit") || "";
    if (!add && !edit) {
      return;
    }
    if (add) {
      setForm({
        event: searchParams.get("event") || "",
        title: "",
        hosted: "",
        start_time: "",
        end_time: "",
      });
      setMenu(null);
      setLogo(null);
      setAddOpen(true);
    }
    if (edit) {
      setPendingEdit(edit);
    }
    setSearchParams({ section: "vip" }, { replace: true });
  }, [searchParams, setSearchParams]);

  useLayoutEffect(() => {
    if (pendingEdit && lunches.some((lunch) => String(lunch.id) === pendingEdit)) {
      setPendingEdit("");
    }
  }, [pendingEdit, lunches]);

  const onChanged = async (message, id, label, name) => {
    if (id) {
      if (!(await ask(removePrompt(name || `this ${label}`)))) {
        return;
      }
      const url =
        label === "luncheon"
          ? `/api/admin/vip-lunches/${id}/`
          : `/api/admin/vip-ticket-types/${id}/`;
      setNotice("");
      setError("");
      try {
        await axios.delete(url, authConfig(token));
        setNotice(`${label} removed.`);
        await load();
      } catch (err) {
        setError(errorText(err));
      }
      return;
    }
    setNotice(message);
    await load();
  };

  const addLunch = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    const formData = new FormData();
    formData.append("event", form.event);
    formData.append("title", form.title);
    formData.append("hosted", form.hosted);
    formData.append("start_time", form.start_time);
    formData.append("end_time", form.end_time);
    if (menu) {
      formData.append("menu", menu);
    }
    if (logo) {
      formData.append("logo", logo);
    }
    try {
      await axios.post("/api/admin/vip-lunches/", formData, authConfig(token));
      setForm({ event: "", title: "", hosted: "", start_time: "", end_time: "" });
      setMenu(null);
      setLogo(null);
      setFileKey((current) => current + 1);
      setAddOpen(false);
      setNotice("Luncheon saved.");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <>
      {confirmDialog}
      <h2>VIP luncheon</h2>
      <p>Menu, logo, host, start and end time, and ticket prices. Attach a luncheon to an event from that event.</p>
      {notice && <Message variant="success">{notice}</Message>}
      {error && <Message variant="danger">{error}</Message>}
      <AdminAddButton
        onClick={() => {
          setForm({ event: "", title: "", hosted: "", start_time: "", end_time: "" });
          setMenu(null);
          setLogo(null);
          setAddOpen(true);
        }}
      >
        Add a luncheon
      </AdminAddButton>
      {events.length === 0 && (
        <Message variant="info" className="mt-3">Add an event before a luncheon can be saved.</Message>
      )}
      <AdminFormModal show={addOpen} size="lg" title="Add a luncheon" onHide={() => setAddOpen(false)}>
      <Form onSubmit={addLunch}>
        <Form.Group className="mb-3" controlId="vipEvent">
          <Form.Label>Event</Form.Label>
          <Form.Select
            value={form.event}
            onChange={(e) => setForm((current) => ({ ...current, event: e.target.value }))}
            required
          >
            <option value="">Select an event</option>
            {events.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="vipTitle">
          <Form.Label>Title</Form.Label>
          <Form.Control
            value={form.title}
            onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="vipHost">
          <Form.Label>Host</Form.Label>
          <Form.Control
            value={form.hosted}
            onChange={(e) => setForm((current) => ({ ...current, hosted: e.target.value }))}
          />
        </Form.Group>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="vipStart">
              <Form.Label>Starts</Form.Label>
              <Form.Control
                type="time"
                value={form.start_time}
                onChange={(e) => setForm((current) => ({ ...current, start_time: e.target.value }))}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="vipEnd">
              <Form.Label>Ends</Form.Label>
              <Form.Control
                type="time"
                value={form.end_time}
                onChange={(e) => setForm((current) => ({ ...current, end_time: e.target.value }))}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3" controlId="vipMenu">
          <Form.Label>Menu</Form.Label>
          <Form.Control
            key={`vip-menu-${fileKey}`}
            type="file"
            onChange={(e) => setMenu(e.target.files?.[0] || null)}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="vipLogo">
          <Form.Label>Logo</Form.Label>
          <Form.Control
            key={`vip-logo-${fileKey}`}
            type="file"
            accept="image/*"
            onChange={(e) => setLogo(e.target.files?.[0] || null)}
          />
        </Form.Group>
        <AdminFormActions saveLabel="Save luncheon" disabled={events.length === 0} onCancel={() => setAddOpen(false)} />
      </Form>
      </AdminFormModal>
      {lunches.length === 0 ? (
        <Message variant="info" className="mt-4">No luncheons yet.</Message>
      ) : (
        <div className="mt-4">
          {lunches.map((lunch) => (
            <LunchEditor
              key={lunch.id}
              lunch={lunch}
              token={token}
              onChanged={onChanged}
              startOpen={String(lunch.id) === pendingEdit}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default AdminVip;
